import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilter, TFilters, TOrderBy } from 'redux/types';
import {
	getCountryVatRateListRequest,
	syncCountryVatRatesRequest,
} from 'redux/reducers/taxDuties/reducer';
import {
	getCountryVatRateList,
	getCountryVatRateListLoading,
	getCountryVatRateSyncLoading,
	getCountryVatRateUploadLoading,
} from 'redux/reducers/taxDuties/selectors';
import { TCountryVatRateListRequest } from 'redux/reducers/taxDuties/types';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { getUserIsAdmin } from 'redux/reducers/user/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TCountryVatRateRow } from './types';
import { UploadRatesDialog } from './UploadRatesDialog';
import { RateFilter } from './RateFilter';

/** The country picker yields an ISO alpha-2 code, while the endpoint filters on the country id. */
const COUNTRY_FILTER_SELECTOR = 'country';
const COUNTRY_ID_FIELD_NAME = 'countryId';
const RATE_SELECTOR = 'rate';

export const EuVatRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

	const list = useSelector(getCountryVatRateList);
	const listLoading = useSelector(getCountryVatRateListLoading);
	const syncLoading = useSelector(getCountryVatRateSyncLoading);
	const countries = useSelector(getCountryListData);
	const userIsAdmin = useSelector(getUserIsAdmin);
	const uploadFileLoading = useSelector(getCountryVatRateUploadLoading);

	useEffect(() => {
		if (!countries) dispatch(getCountryListRequest());
	}, [countries, dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const listRequest = useMemo<TCountryVatRateListRequest>(() => {
		const filters = (
			getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters
		).flatMap<TFilter>((filter) => {
			if (filter.fieldName !== COUNTRY_FILTER_SELECTOR) return filter;
			const countryId = countries?.find((item) => item.isoAlpha2 === filter.value)?.id;
			if (!countryId) return [];
			return {
				...filter,
				fieldName: COUNTRY_ID_FIELD_NAME,
				name: COUNTRY_ID_FIELD_NAME,
				value: String(countryId),
			};
		});

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		return {
			pageNumber: params.page ? +params.page : 1,
			pageSize: PAGE_SIZE,
			searchString: String(params?.search ?? ''),
			filters,
			orderBy,
		};
	}, [countries, params]);

	useEffect(() => {
		dispatch(getCountryVatRateListRequest(listRequest));
	}, [dispatch, listRequest]);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TCountryVatRateRow>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const vatRates = list?.vatRates;
	const hasVatRates = !!vatRates?.length;

	/**
	 * With the rates known, the rate is picked by the buttons of `RateFilter` instead of the column's
	 * separate filter. Both keep the value in the same `f-rate` param, so the request stays the same.
	 */
	const displayedTableStructure = useMemo(
		() =>
			hasVatRates
				? currentTableStructure.map((column) =>
						column.selector === RATE_SELECTOR ? { ...column, separateFilter: false } : column,
					)
				: currentTableStructure,
		[currentTableStructure, hasVatRates],
	);

	const handleSyncConfirm = useCallback(() => {
		setSyncConfirmOpen(false);
		dispatch(syncCountryVatRatesRequest());
	}, [dispatch]);

	return (
		<>
			<ConfirmationDialog
				open={syncConfirmOpen}
				setClose={() => setSyncConfirmOpen(false)}
				handleConfirm={handleSyncConfirm}
				title="Sync EU VAT Rates"
				text="The rates will be fetched from the rate provider and stored. Do you want to continue?"
			/>
			<UploadRatesDialog
				open={uploadDialogOpen}
				onClose={() => setUploadDialogOpen(false)}
				listRequest={listRequest}
			/>
			<EasTableWithFilter<TCountryVatRateRow>
				tableStructure={displayedTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={() => undefined}
				onTableStructureChange={handleTableStructureChange}
				filterChildrenToStart={
					hasVatRates ? <RateFilter rates={vatRates ?? []} selector={RATE_SELECTOR} /> : undefined
				}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / Tax & Duties / <b>EU VAT Rates</b>
					</Typography>
					{userIsAdmin && (
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={() => setUploadDialogOpen(true)}
								startIcon={uploadFileLoading ? <ButtonCircularProgress /> : <UploadFileIcon />}
								disabled={uploadFileLoading || listLoading}
							>
								Upload file
							</Button>
							<Button
								variant="contained"
								color="primary"
								size="small"
								disabled={syncLoading || listLoading}
								onClick={() => setSyncConfirmOpen(true)}
								startIcon={syncLoading ? <ButtonCircularProgress /> : <SyncIcon />}
							>
								Sync
							</Button>
						</Box>
					)}
				</Box>
			</EasTableWithFilter>
		</>
	);
};
